import { useApolloClient } from "@apollo/client";
import { getDataOrNull, stripHtmlTags } from "@msys/common";
import { LexicalRichTextEditorWithHtml, MenuButton } from "@msys/ui";
import {
  MemberEmailRenderContext,
  renderMemberEmailSubject,
  renderMemberEmailTemplate,
} from "@msys/textutils";
import { FindReplace as FindReplaceIcon } from "@mui/icons-material";
import { FormHelperText, MenuItem, Stack, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import React from "react";
import * as Yup from "yup";
import { useAuthenticatedViewer } from "../../auth/useUserData.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import {
  OrganisationMembershipInvitationEmailForm_TemplateFragment,
  useOrganisationMembershipInvitationEmailTemplatesQuery,
} from "./useOrganisationMembershipInvitationEmailForm.generated.js";

export interface OrganisationMembershipInvitationEmailFormValues {
  subject: string;
  message: string;
  templateId: string;
}

export interface MemberInvitee {
  name: string;
  firstName: string;
  familyName: string;
}

type MemberPrefillSource = { subject: string; content: string };

// Only a saved template is Handlebars source. The legacy default reaches here
// already ICU-interpolated with the organisation title and the inviter's name,
// so rendering it would make braces those values happen to contain executable:
// an organisation named `{{member.name}}` is silently renamed to the invitee,
// and an unmatched `{{` throws out of the prefill and keeps the form from
// opening at all.
type MemberPrefillSelection = MemberPrefillSource & { isTemplate: boolean };

// Picks what to prefill the form with: the explicitly-selected template, or the
// legacy default until one is picked. A saved template is never applied
// implicitly — MEMBER is a context, not a use case, so nothing about a stored
// template says it was written for an invitation.
// Returns null only when a selected templateId no longer matches an available
// template (stale selection).
export function selectMemberPrefillSource({
  templateId,
  availableTemplates,
  legacySubject,
  legacyBody,
}: {
  templateId: string;
  availableTemplates: OrganisationMembershipInvitationEmailForm_TemplateFragment[];
  legacySubject: string;
  legacyBody: string;
}): MemberPrefillSelection | null {
  if (templateId) {
    const tmpl = availableTemplates.find(t => t.id === templateId);
    if (!tmpl) return null;
    return { subject: tmpl.subject, content: tmpl.content, isTemplate: true };
  }

  return {
    subject: legacySubject,
    content: legacyBody,
    isTemplate: false,
  };
}

function renderMemberPrefillSource(
  selection: MemberPrefillSelection,
  ctx: MemberEmailRenderContext
): MemberPrefillSource {
  const { subject, content, isTemplate } = selection;
  if (!isTemplate) return { subject, content };

  return {
    // The subject is a plain-text header, the content is HTML — they need
    // different escaping.
    subject: renderMemberEmailSubject(subject, ctx),
    content: renderMemberEmailTemplate(content, ctx),
  };
}

// The body editor rewrites a plain-text prefill (the legacy default) into
// paragraph markup on mount. Absorb exactly that rewrite into the baseline, so
// the edit check stays a verbatim comparison and a later formatting-only change
// — bolding a word, retargeting a link, splitting a paragraph — still counts as
// an edit even though the wording is unchanged.
//
// Comparing stripped text instead would make the tolerance permanent for a
// plain-text prefill, and every formatting-only edit to the legacy default
// would read as unedited and be overwritten by the next re-render.
function absorbEditorRewrite(
  applied: MemberPrefillSource,
  current: string
): void {
  if (applied.content === current) return;
  if (containsMarkup(applied.content)) return;
  if (toComparableText(applied.content) !== toComparableText(current)) return;
  applied.content = current;
}

// A cleared rich-text editor still serialises structural markup (`<p><br></p>`),
// which is a non-empty string and so passes `required()`. The body counts as
// filled when it has visible text or embedded media — an image-only message is
// legitimate, so emptiness cannot be decided on text alone.
function hasBodyVisibleContent(value: string | undefined): boolean {
  if (!value) return false;
  return toComparableText(value) !== "" || EMBEDDED_MEDIA.test(value);
}

const EMBEDDED_MEDIA = /<(img|video|audio|iframe|embed|object|table)\b/i;

function containsMarkup(value: string): boolean {
  return /<[^>]+>/.test(value);
}

function toComparableText(value: string): string {
  return stripHtmlTags(value, false).replace(/\s+/g, " ").trim();
}

// What the prefill effect has to remember between runs. It lives in the parent
// hook rather than in the fields component because the create-new-member modal
// renders the fields behind the "Invite to use MeisterSystems" checkbox: the
// component unmounts when it is unticked while Formik keeps the edited values,
// so component-local bookkeeping would come back empty and re-prefill over
// them.
export interface MemberPrefillState {
  lastApplied: MemberPrefillSource | null;
  lastPrefillKey: string | null;
  lastSourceKey: string | null;
}

export interface OrganisationMembershipInvitationEmailFieldsData {
  availableTemplates: OrganisationMembershipInvitationEmailForm_TemplateFragment[];
  legacySubject: string;
  legacyBody: string;
  templatesLoading: boolean;
  templatesError: string | null;
  prefillState: React.MutableRefObject<MemberPrefillState>;
}

export function OrganisationMembershipInvitationEmailFields({
  invitee,
  availableTemplates,
  legacySubject,
  legacyBody,
  prefillState,
}: OrganisationMembershipInvitationEmailFieldsData & {
  invitee: MemberInvitee;
}) {
  const { t } = useTranslate(["OrganisationInvite", "Email"]);
  const viewer = useAuthenticatedViewer();
  const formikProps =
    useFormikContext<OrganisationMembershipInvitationEmailFormValues>();
  const setFieldValue = formikProps.setFieldValue;
  const templateId = formikProps.values.templateId;

  const inviterName = (viewer.firstname + " " + viewer.familyname).trim();
  const renderContext = React.useMemo<MemberEmailRenderContext>(
    () => ({
      member: {
        name: invitee.name,
        firstName: invitee.firstName,
        familyName: invitee.familyName,
      },
      organisation: { name: viewer.organisation.title },
      user: { name: inviterName },
    }),
    [
      invitee.name,
      invitee.firstName,
      invitee.familyName,
      viewer.organisation.title,
      inviterName,
    ]
  );

  // The body editor reads `initialHtmlValue` only on mount, so it is keyed on
  // this counter: every prefill writes the new content and bumps the key in the
  // same batch, remounting the editor onto the value it just wrote.
  const [prefillGeneration, setPrefillGeneration] = React.useState(0);

  const applyPrefill = React.useCallback(
    (selection: MemberPrefillSelection) => {
      const rendered = renderMemberPrefillSource(selection, renderContext);
      prefillState.current.lastApplied = rendered;
      setFieldValue("subject", rendered.subject);
      setFieldValue("message", rendered.content);
      setPrefillGeneration(generation => generation + 1);
    },
    [prefillState, renderContext, setFieldValue]
  );

  // Interpolation happens at prefill time (mirrors SendQuoteViaEmailModal): the
  // fields hold already-rendered text the inviter can edit. Idempotent per
  // (source, invitee) so template refetches don't clobber edits, while a
  // changing invitee (the create-member flow types the name) re-renders.
  const inviteeKey = `${invitee.firstName}|${invitee.familyName}|${invitee.name}`;
  const sourceKey = templateId ? `template:${templateId}` : "legacy";
  const prefillKey = `${sourceKey}|${inviteeKey}`;

  const subject = formikProps.values.subject;
  const message = formikProps.values.message;
  React.useEffect(() => {
    const source = selectMemberPrefillSource({
      templateId,
      availableTemplates,
      legacySubject,
      legacyBody,
    });
    if (!source) return;

    const state = prefillState.current;
    const applied = state.lastApplied;

    // Runs before the key check below: the editor's rewrite arrives after the
    // prefill that triggered it, under that same key.
    if (applied !== null) absorbEditorRewrite(applied, message);

    if (state.lastPrefillKey === prefillKey) return;

    // Picking a template is explicit intent and always wins. A changing invitee
    // only re-renders the placeholders (the create-member flow types the name),
    // so it must leave text the inviter has since edited alone.
    const edited =
      applied !== null &&
      (subject !== applied.subject || message !== applied.content);
    if (state.lastSourceKey === sourceKey && edited) return;

    state.lastPrefillKey = prefillKey;
    state.lastSourceKey = sourceKey;

    applyPrefill(source);
  }, [
    templateId,
    availableTemplates,
    legacySubject,
    legacyBody,
    prefillKey,
    sourceKey,
    subject,
    message,
    prefillState,
    applyPrefill,
  ]);

  return (
    <Stack direction="column" spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <TextField
          name="subject"
          label={t("Subject", { ns: "Email" })}
          required
          clearable
        />
        {availableTemplates.length > 0 && (
          <MenuButton
            Icon={
              <Tooltip title={t("Apply template", { ns: "Email" })}>
                <FindReplaceIcon />
              </Tooltip>
            }
            buttonProps={{ size: "medium" }}
          >
            {availableTemplates.map(template => (
              <MenuItem
                key={template.id}
                onClick={() => {
                  // Re-apply when the same template is picked again, otherwise
                  // the prefill effect handles the templateId change.
                  if (templateId === template.id) {
                    applyPrefill({
                      subject: template.subject,
                      content: template.content,
                      isTemplate: true,
                    });
                  }
                  setFieldValue("templateId", template.id);
                }}
              >
                {template.description}
              </MenuItem>
            ))}
          </MenuButton>
        )}
      </Stack>
      {/* MEMBER templates store `content` as HTML (authored with the same
          editor in OrganisationEmailTemplateModal) and the invitation handler
          injects `messageBody` straight into the HTML layout, so the body is
          edited as rich text — as in CrmSendEmailForm. */}
      <LexicalRichTextEditorWithHtml
        key={prefillGeneration}
        placeholder={t("Personal message", { ns: "OrganisationInvite" })}
        initialHtmlValue={formikProps.values.message}
        handleChange={value => {
          setFieldValue("message", value);
        }}
      />
      {/* Not gated on `touched`: the editor writes through `setFieldValue`, so
          this field never blurs and never becomes touched. Without this an
          emptied body leaves Invite disabled with no message anywhere on the
          form — as in OrganisationEmailTemplateModal. */}
      {formikProps.errors.message && (
        <FormHelperText error>{formikProps.errors.message}</FormHelperText>
      )}
    </Stack>
  );
}

export function useOrganisationMembershipInvitationEmailForm({
  invitee,
}: {
  invitee: MemberInvitee;
}) {
  const viewer = useAuthenticatedViewer();
  const { t } = useTranslate(["OrganisationInvite", "Email"]);
  const client = useApolloClient();

  const query = useOrganisationMembershipInvitationEmailTemplatesQuery({
    client,
  });
  const availableTemplates = React.useMemo(
    () =>
      getDataOrNull(query.data?.organisationEmailTemplates)?.emailTemplates ??
      [],
    [query.data?.organisationEmailTemplates]
  );
  const templatesLoading = query.loading && !query.previousData;
  // A failed query must not look like "this organisation has no templates" —
  // that would let the inviter send the legacy default over a saved template.
  const templatesError =
    query.error && !query.data && !query.previousData
      ? query.error.message
      : null;

  const inviterName = (viewer.firstname + " " + viewer.familyname).trim();

  const legacySubject = t("Join {organisationName} on MeisterSystems", {
    ns: "OrganisationInvite",
    organisationName: viewer.organisation.title,
  });
  const legacyBody = t(
    "Hello, Join our organisation {organisationName} on MeisterSystems. Kind regards, {senderName}",
    {
      ns: "OrganisationInvite",
      organisationName: viewer.organisation.title,
      senderName: inviterName,
    }
  );

  // Seed with the synchronously-available legacy default so the required fields
  // are valid from the first render; the prefill effect overwrites them only
  // once the inviter picks a template. Legacy strings are applied verbatim, so
  // this seed is exactly what the effect would apply.
  const initialValues =
    React.useMemo<OrganisationMembershipInvitationEmailFormValues>(
      () => ({
        subject: legacySubject,
        message: legacyBody,
        templateId: "",
      }),
      [legacySubject, legacyBody]
    );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        subject: Yup.string()
          .label(t("Subject", { ns: "Email" }))
          .trim()
          .required(),
        message: Yup.string()
          .label(t("Personal message", { ns: "OrganisationInvite" }))
          // Collapse a body with no visible text or media to "" so `required`
          // rejects it with its own message, rather than hand-rolling a `.test`.
          .transform(value => (hasBodyVisibleContent(value) ? value : ""))
          .required(),
      }),
    [t]
  );

  // Held here, not in the fields component, so it survives the create-new-member
  // modal unmounting those fields when "Invite to use MeisterSystems" is
  // unticked — Formik keeps the values, so the bookkeeping has to keep up.
  const prefillState = React.useRef<MemberPrefillState>({
    lastApplied: null,
    lastPrefillKey: null,
    lastSourceKey: null,
  });

  const emailFieldsData: OrganisationMembershipInvitationEmailFieldsData = {
    availableTemplates,
    legacySubject,
    legacyBody,
    templatesLoading,
    templatesError,
    prefillState,
  };

  const formFields = (
    <OrganisationMembershipInvitationEmailFields
      invitee={invitee}
      {...emailFieldsData}
    />
  );

  return {
    formFields,
    emailFieldsData,
    initialValues,
    validationSchema,
    templatesLoading,
    templatesError,
  };
}
